import { ActionModal, FormLayout } from '@kcftech/design-system';
import { ICorporationResponse, LocationCreateRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { CorporationDropdown } from 'components/dropdown/CorporationDropdown';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { sdClient } from 'contexts/SDKClientContext';
import { infiniteLocationsDataSourceKey } from 'custom_hooks/data_hooks/data_source/locationsInfiniteDataSource';
import { maxLocationNameLength } from 'pages/Settings/Locations/shared';
import { netSuiteIdRules } from 'pages/Settings/shared/shared';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import { extractErrorString } from 'utils/api';
import { queryClient } from 'utils/query';

export type CreateLocationModalProps = Readonly<{
    onCreate: () => void;
    onClose: () => void;
}>;

type CreateLocationForm = Readonly<{
    name: string;
    corporation: ICorporationResponse;
    netSuiteId: string | undefined;
}>;

const nameRules: RegisterOptions = {
    required: 'Please enter the location name',
    maxLength: {
        value: maxLocationNameLength,
        message: `Location name cannot be greater than ${maxLocationNameLength} characters`,
    },
};

const corporationRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Please select a corporation',
    },
};

export function CreateLocationModal({ onCreate, onClose }: CreateLocationModalProps) {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateLocationForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: { corporation: useRouteEntities().corporation },
    });
    const { showToast } = useNotificationContext();

    const { mutate: onSave } = useMutation({
        meta: { name: 'create-location' },
        mutationFn: async (data: CreateLocationForm) => {
            await sdClient.createLocation({
                body: new LocationCreateRequest({
                    CorporationId: data.corporation.Id,
                    Name: data.name,
                    NetSuiteId: data.netSuiteId,
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Location created successfully',
            });

            queryClient.invalidateQueries({
                queryKey: [infiniteLocationsDataSourceKey],
            });
            onCreate();
        },

        onError: (error) => {
            showToast({
                type: 'error',
                content: convertError(extractErrorString(error)),
            });
            onCreate();
        },
    });

    return (
        <ActionModal
            onAction={handleSubmit((data) => onSave(data))}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onClose={onClose}
            title="Create Location"
            actionButtonText="Create"
            closeButtonText="Cancel"
        >
            <FormLayout>
                <ValidatableLabel label="Location Name" error={errors.name} required>
                    <TextInput
                        autoFocus
                        placeholder="Enter a name for the location"
                        invalid={errors.name !== undefined}
                        {...register('name', nameRules)}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="Corporation Name"
                    required
                    error={errors.corporation?.message}
                >
                    <Controller
                        name="corporation"
                        control={control}
                        rules={corporationRules}
                        render={({ field: { value, onChange } }) => (
                            <CorporationDropdown value={value} onChange={onChange} />
                        )}
                    />
                </ValidatableLabel>
                <ValidatableLabel
                    label="NetSuite ID"
                    ariaLabel="net suite ID"
                    error={errors.netSuiteId}
                >
                    <TextInput
                        placeholder="Enter a NetSuite ID"
                        invalid={errors.netSuiteId !== undefined}
                        {...register('netSuiteId', netSuiteIdRules)}
                    />
                </ValidatableLabel>
            </FormLayout>
        </ActionModal>
    );
}

function convertError(error: string | undefined) {
    if (error === undefined) {
        return 'Failed to create location';
    }

    if (error.includes(' already exists at the corporation ')) {
        return 'A location with that name already exists';
    }

    return error;
}
